// eslint-disable-next-line import/no-cycle
import { IUser, IUserAccountGeneral } from 'src/@types/user';
import { UserStatus } from 'src/@types/user-status';
import { SettingsValueProps } from 'src/components/settings/types';
import { UserRecent } from 'src/hooks/recents';
import { AccessToken } from 'src/state/session';
import { axios } from 'src/utils/axios';

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<boolean> {
  const { status } = await axios.post('/user/security/change-password', {
    currentPassword,
    newPassword,
  });
  return status === 202;
}

export async function setupPassword(newPassword: string): Promise<boolean> {
  const { status } = await axios.post('/user/security/setup-password', {
    newPassword,
  });
  return status === 202;
}

export async function logout() {
  await axios.delete('/system/logout');
}

export type UserSecurityEmail = {
  _id: string;
  email: string;
  verified: boolean;
};

export async function getUserSecurityEmails() {
  const { data } = await axios.get<UserSecurityEmail[]>(
    '/user/security/my-emails',
  );
  return data;
}

export async function addUserSecurityEmail(email: string) {
  const { data } = await axios.post<UserSecurityEmail>(
    '/user/security/my-emails/add',
    {
      email,
    },
  );
  return data;
}

export async function resendUserSecurityEmail(emailId: string) {
  const { data } = await axios.post<void>(
    '/user/security/my-emails/add/resend',
    {
      emailId,
    },
  );
  return data;
}

export async function verifyUserSecurityEmail({
  emailId,
  code,
}: { emailId: string; code: string }) {
  const { data } = await axios.post<UserSecurityEmail[]>(
    '/user/security/my-emails/add/verify',
    {
      emailId,
      code,
    },
  );
  return data;
}

export async function changeUserSecurityEmail(emailId: string) {
  const { data } = await axios.post<{ email: string }>(
    '/user/security/my-emails/change',
    {
      emailId,
    },
  );
  return data;
}

export type LoginResponse = {
  accessTokens: AccessToken[];
  accountToken?: string;
  user: IUser;
  twoFactorEnabled?: boolean;
};

export async function login({
  email,
  password,
  twoFactorCode,
}: {
  email: string;
  password: string;
  twoFactorCode?: string;
}): Promise<LoginResponse> {
  const { data } = await axios.post<LoginResponse>('/system/login', {
    email,
    password,
    twoFactorCode,
  });
  return data;
}

export async function oauthLogin({
  token,
  signup,
  method,
  twoFactorCode,
}: {
  token: string;
  signup: boolean;
  method: 'token_info' | 'user_info';
  twoFactorCode?: string;
}): Promise<LoginResponse> {
  const { data } = await axios.post<LoginResponse>('/system/web/oauth/google', {
    token,
    signup,
    method,
    twoFactorCode,
  });
  return data;
}

export type RegisterResponse = {
  needVerifyEmail: boolean;
};
export async function register({
  name,
  email,
  password,
  workspaceInvite,
  humanKey,
}: {
  name: string;
  email: string;
  password: string;
  workspaceInvite?: string;
  humanKey: string;
}): Promise<RegisterResponse | LoginResponse> {
  const { data } = await axios.post<RegisterResponse | LoginResponse>(
    '/system/register',
    {
      name,
      email,
      password,
      workspaceInvite,
      humanKey,
    },
  );
  return data;
}

export async function resetPassword({
  code,
  password,
}: {
  code: string;
  password: string;
}): Promise<LoginResponse> {
  const { data } = await axios.post<LoginResponse>('/system/reset-password', {
    code,
    password,
  });
  return data;
}

export async function forgotPassword(email: string) {
  const { data } = await axios.post<void>('/system/forgot-password', {
    email,
  });
  return data;
}

export async function resendCode(): Promise<void> {
  await axios.post<void>('/system/resend-verify-email');
}

export type WorkspaceInviteInfo = {
  email?: string;
  workspace: {
    name: string;
    shortName: string;
    picture: string;
  };
};

export async function getWorkspaceInviteInfo(
  invite: string,
): Promise<WorkspaceInviteInfo> {
  const { data } = await axios.get<WorkspaceInviteInfo>(
    `/system/invite?workspaceInvite=${invite}`,
  );
  return data;
}

export async function verifyAccount(code: string): Promise<LoginResponse> {
  const { data } = await axios.post<LoginResponse>('/system/verify-email', {
    code,
  });
  return data;
}

export async function updateSettings(
  settings: SettingsValueProps,
): Promise<void> {
  await axios.post('/user/settings', settings);
}

export async function getInfo(): Promise<IUser> {
  const { data } = await axios.get<IUser>('/user/info');
  return data;
}

type IUserAccountGeneralToUpdate = Omit<
  IUserAccountGeneral,
  | 'status'
  | 'isVerified'
  | 'role'
  | 'company'
  | 'id'
  | 'zipCode'
  | 'country'
  | 'address'
  | 'city'
  | 'state'
  | 'email'
>;

export async function updateUserAccount(
  accountData: IUserAccountGeneralToUpdate,
): Promise<IUserAccountGeneralToUpdate> {
  const { data } = await axios.put<IUserAccountGeneralToUpdate>(
    '/user/account',
    accountData,
  );
  return data;
}

type EnableTwoFactorResponse = {
  qrCode: string;
  base32: string;
};

export async function enableTwoFactor(): Promise<EnableTwoFactorResponse> {
  const { data } = await axios.post<EnableTwoFactorResponse>(
    '/admin/twofactor/enable',
  );
  return data;
}

export async function disableTwoFactor(): Promise<boolean> {
  const { status } = await axios.delete('/admin/twofactor/disable');
  return status === 202;
}

export async function firstVerify(
  token: string,
  isWizard?: boolean,
): Promise<boolean> {
  const { status } = await axios.post('/admin/twofactor/first-verify', {
    token,
    isWizard,
  });
  return status === 202;
}

export type StatusChangerPayload = {
  text?: string;
  icon?: string;
  status?: UserStatus;
};

export async function updateUserStatus(
  customStatus: StatusChangerPayload,
): Promise<IUser> {
  const { data } = await axios.put<IUser>('/user/status', customStatus);
  return data;
}

export type GetUserRecentsParams = {
  skip?: number;
  limit?: number;
  q?: string;
  sort?: string;
};

export async function getUserRecents(params?: GetUserRecentsParams) {
  const { data } = await axios.get<UserRecent[]>('/user-recents', {
    params,
  });
  return data;
}

export async function upsertUserRecent(
  recent: Partial<UserRecent>,
): Promise<UserRecent> {
  const { data } = await axios.put<UserRecent>('/user-recents', recent);
  return data;
}

export async function removeUserRecent(id: string): Promise<void> {
  const { data } = await axios.delete(`/user-recents/${id}`);
  return data;
}

export type UserLoginHistory = {
  _id: string;
  ip: string;
  city: string;
  country: string;
  region: string;
  ua: string;
  lastAccess: string;
  logout?: string;
  createdAt: string;
};

export type SecurityInfo = {
  needSetupPassword: boolean;
  loginHistory: UserLoginHistory[];
};

export async function getSecurityInfo(): Promise<SecurityInfo> {
  const { data } = await axios.get<SecurityInfo>('/user/security-info');
  return data;
}

export * as UserRequest from './user';
